import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const LockedSection = ({
  title,
  subtitle,
  description,
  ctaText,
  ctaLink,
  features = [
    "Live interactive classes with expert instructors",
    "Extensive library of recorded sessions",
    "Personalized study materials and resources",
    "Progress tracking and performance analytics",
    "Direct Q&A with subject matter experts",
    "Mobile-friendly access anytime, anywhere",
  ],
  variant = "premium",
  isAuthenticated = false, // Pass this prop to check if user is logged in
  userToken = null, // Pass user token or auth state
}) => {
  const { currentUser, userDetails, signOut } = useAuth();

  // Determine if user is signed in (you can also check localStorage token here)
  const isUserSignedIn = currentUser ? true : false;

  // Set content based on authentication status
  const getContentBasedOnAuth = () => {
    if (!isUserSignedIn) {
      return {
        title: title || "Sign In Required",
        subtitle: subtitle || "Please login to access premium content",
        description:
          description ||
          "You need to be signed in to access our live classes, recorded sessions, and premium study materials. Join thousands of students already learning with us.",
        ctaText: ctaText || "Sign In Now",
        ctaLink: ctaLink || "/login",
        secondaryCtaText: "Create Account",
        secondaryCtaLink: "/register",
        icon: "login",
      };
    } else {
      return {
        title: title || "Premium Content Locked",
        subtitle: subtitle || "Unlock access to exclusive content",
        description:
          description ||
          "Get unlimited access to live classes, recorded sessions, and premium study materials to accelerate your learning journey.",
        ctaText: ctaText || "Browse Courses",
        ctaLink: ctaLink || "/classroom",
        secondaryCtaText: "View Pricing",
        secondaryCtaLink: "/pricing",
        icon: "lock",
      };
    }
  };

  const content = getContentBasedOnAuth();

  const getVariantStyles = () => {
    switch (variant) {
      case "gradient":
        return {
          container: "bg-gradient-to-br from-indigo-50 via-white to-purple-50",
          border: "border-indigo-200",
          lockBg: "bg-gradient-to-br from-[#2C5282] to-purple-600",
          titleColor: "text-gray-900",
          subtitleColor: "text-[#2C5282]",
          descColor: "text-gray-600",
          featureBg: "bg-white/70",
          ctaButton:
            "bg-gradient-to-r from-[#2C5282] to-blue-700 hover:from-[#2A4A7A] hover:to-blue-800",
        };
      case "simple":
        return {
          container: "bg-gray-50",
          border: "border-gray-200",
          lockBg: "bg-[#2C5282]",
          titleColor: "text-gray-900",
          subtitleColor: "text-gray-600",
          descColor: "text-gray-600",
          featureBg: "bg-white",
          ctaButton: "bg-[#2C5282] hover:bg-[#2A4A7A]",
        };
      default: // premium
        return {
          container:
            "bg-gradient-to-br from-blue-50 via-indigo-50 to-[#2C5282]/10",
          border: "border-[#2C5282]/30",
          lockBg: "bg-gradient-to-br from-[#2C5282] to-blue-700",
          titleColor: "text-gray-900",
          subtitleColor: "text-[#2C5282]",
          descColor: "text-gray-700",
          featureBg: "bg-white/80",
          ctaButton:
            "bg-gradient-to-r from-[#2C5282] to-blue-600 hover:from-[#2A4A7A] hover:to-blue-700",
        };
    }
  };

  const styles = getVariantStyles();

  // Icon component based on authentication status
  const renderIcon = () => {
    if (content.icon === "login") {
      return (
        <svg
          className="w-10 h-10 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
          />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-10 h-10 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`relative overflow-hidden rounded-2xl border-2 ${styles.border} ${styles.container} p-8 md:p-12`}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='5'/%3E%3Ccircle cx='53' cy='7' r='5'/%3E%3Ccircle cx='53' cy='53' r='5'/%3E%3Ccircle cx='7' cy='53' r='5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>
      <div style={{height:70}}/>
      <div className="relative z-10 text-center max-w-4xl mx-auto">
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto mb-8"
        >
          <div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${styles.lockBg} shadow-lg`}
          >
            {renderIcon()}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h2
            className={`text-3xl md:text-4xl font-bold ${styles.titleColor} mb-4`}
          >
            {content.title}
          </h2>
          <p className={`text-xl ${styles.subtitleColor} font-semibold mb-6`}>
            {content.subtitle}
          </p>
          <p
            className={`text-lg ${styles.descColor} mb-10 max-w-2xl mx-auto leading-relaxed`}
          >
            {content.description}
          </p>
        </motion.div>

        {/* Authentication-specific content */}
        {!isUserSignedIn ? (
          // Not signed in - show login benefits
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div
                className={`${styles.featureBg} backdrop-blur-sm rounded-lg p-6 border border-[#2C5282]/20 shadow-sm`}
              >
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-[#2C5282]/10 rounded-lg flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-[#2C5282]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    Free Account Benefits
                  </h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Access to free sample classes</li>
                  <li>• Course previews and syllabus</li>
                  <li>• Study material downloads</li>
                  <li>• Community forum access</li>
                </ul>
              </div>

              <div
                className={`${styles.featureBg} backdrop-blur-sm rounded-lg p-6 border border-[#2C5282]/20 shadow-sm`}
              >
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#2C5282] to-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    Premium Features
                  </h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Live interactive classes</li>
                  <li>• Full recorded sessions library</li>
                  <li>• 1-on-1 doubt sessions</li>
                  <li>• Progress tracking & analytics</li>
                </ul>
              </div>
            </div>
          </motion.div>
        ) : (
          // Signed in but no enrollment - show course features
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className={`${styles.featureBg} backdrop-blur-sm rounded-lg p-4 border border-[#2C5282]/20 shadow-sm`}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Link
            to={content.ctaLink}
            className={`inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-semibold rounded-xl text-white ${styles.ctaButton} transform transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl`}
          >
            {!isUserSignedIn ? (
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            )}
            {content.ctaText}
          </Link>

          <Link
            to={content.secondaryCtaLink}
            className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#2C5282] text-base font-medium rounded-xl text-[#2C5282] bg-white hover:bg-[#2C5282]/5 transition-colors duration-200"
          >
            {content.secondaryCtaText}
          </Link>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
          className="mt-8 pt-8 border-t border-gray-200"
        >
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1 text-green-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {!isUserSignedIn ? "Free Registration" : "Secure Payment"}
            </div>
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {!isUserSignedIn ? "Instant Access" : "Money-back Guarantee"}
            </div>
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1 text-[#2C5282]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              24/7 Support
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LockedSection;
